import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useWebAuth } from '../context/WebAuthContext';
import LoginModal from '../components/LoginModal';
import { useToast } from '../context/ToastContext';
import IranAddressPicker from '../components/address/IranAddressPicker';

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fa-IR');
}

const statusLabel = {
  customer_submitted: 'ثبت شده توسط مشتری',
  registered: 'ثبت شده',
  pending: 'در انتظار بررسی',
  expert_viewed: 'بازدید توسط کارشناس',
  viewed_by_expert: 'بازدید توسط کارشناس',
  accepted: 'تایید شده',
  rejected: 'رد شده',
  finalized: 'ثبت شده',
  cancelled: 'لغو شده',
};

export default function AccountPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthed, user, token, updateUser, logout } = useWebAuth();
  const { showToast } = useToast();
  const [loginOpen, setLoginOpen] = useState(false);
  const [tab, setTab] = useState('preinvoices'); // preinvoices | profile
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [addressPickerOpen, setAddressPickerOpen] = useState(false);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', status: '' });

  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: '',
    province_code: '',
    province_name: '',
    county_id: '',
    county_name: '',
    address_line: '',
    postal_code: '',
  });

  useEffect(() => {
    if (!isAuthed) {
      setLoginOpen(true);
      return;
    }
    setProfile({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      province_code: user?.province_code || '',
      province_name: user?.province_name || '',
      county_id: user?.county_id ? String(user.county_id) : '',
      county_name: user?.county_name || '',
      address_line: user?.address_line || '',
      postal_code: user?.postal_code || '',
    });
    if (!user?.profile_completed_at) setTab('profile');
  }, [isAuthed, user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'preinvoices') setTab('preinvoices');
    if (params.get('created') === '1') {
      showToast({
        title: 'سفارش شما با موفقیت ثبت شد',
        message: 'همکاران ما به زودی با شما تماس خواهند گرفت.',
      });
      navigate('/account?tab=preinvoices', { replace: true });
    }
  }, [location.search, navigate, showToast]);

  useEffect(() => {
    if (!isAuthed) return;
    setLoading(true);
    api.webPreinvoices(token, filters)
      .then((res) => setList(res?.data || []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, [isAuthed, token, filters]);

  const fullName = useMemo(() => {
    const n = [user?.first_name, user?.last_name].filter(Boolean).join(' ');
    return n || user?.phone || 'کاربر';
  }, [user]);

  async function saveProfile() {
    if (!isAuthed) return;
    if (!profile.first_name.trim() || !profile.last_name.trim() || !profile.county_id || !profile.address_line.trim()) {
      showToast({ title: 'اطلاعات ناقص', message: 'نام، نام خانوادگی، استان، شهرستان و نشانی را کامل کنید.' });
      return;
    }
    if (profile.postal_code && !/^\d{10}$/.test(profile.postal_code)) {
      showToast({ title: 'کد پستی نامعتبر', message: 'کد پستی باید دقیقاً ۱۰ رقم باشد.' });
      return;
    }
    setLoading(true);
    try {
      const res = await api.webUpdateProfile(token, profile);
      if (res?.data) updateUser(res.data);
      showToast({ title: 'ذخیره شد', message: 'پروفایل شما با موفقیت بروزرسانی شد.' });
      // revalidate is handled by provider (or keep local)
    } catch (err) {
      showToast({ title: 'خطا', message: err.message || 'ذخیره ناموفق بود.' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="bg-gray-50 min-h-[70vh]" dir="rtl">
      <LoginModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onSuccess={() => setLoginOpen(false)}
      />
      <IranAddressPicker
        open={addressPickerOpen}
        value={profile}
        onClose={() => setAddressPickerOpen(false)}
        onConfirm={(selection) => {
          setProfile((current) => ({ ...current, ...selection }));
          setAddressPickerOpen(false);
        }}
      />

      <div className="pp-container py-8">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-extrabold text-gray-900">پنل کاربری</h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">سلام، {fullName}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/preinvoice"
              className="px-4 py-2 rounded-full bg-[#005bbb] text-white text-xs sm:text-sm font-semibold hover:bg-[#004a99]"
            >
              ایجاد پیش‌فاکتور
            </Link>
            <button
              onClick={() => {
                logout();
                showToast({ title: 'خروج انجام شد', message: 'برای ثبت پیش‌فاکتور دوباره وارد شوید.' });
                navigate('/');
              }}
              className="px-4 py-2 rounded-full bg-white border border-gray-200 text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              خروج
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <aside className="lg:col-span-4">
            <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-4">
              <button
                onClick={() => setTab('preinvoices')}
                className={`w-full text-right px-4 py-3 rounded-2xl text-sm font-bold transition ${
                  tab === 'preinvoices' ? 'bg-blue-50 text-[#005bbb]' : 'hover:bg-gray-50 text-gray-800'
                }`}
              >
                پیش‌فاکتورهای من
              </button>
              <button
                onClick={() => setTab('profile')}
                className={`mt-2 w-full text-right px-4 py-3 rounded-2xl text-sm font-bold transition ${
                  tab === 'profile' ? 'bg-blue-50 text-[#005bbb]' : 'hover:bg-gray-50 text-gray-800'
                }`}
              >
                پروفایل
              </button>
            </div>
          </aside>

          <section className="lg:col-span-8">
            {tab === 'preinvoices' ? (
              <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                  <div className="font-extrabold text-gray-900">سوابق پیش‌فاکتور</div>
                  <div className="text-xs text-gray-500">{list.length} مورد</div>
                </div>
                <div className="grid gap-3 border-b border-gray-100 px-5 py-4 sm:grid-cols-3">
                  <label className="space-y-1">
                    <span className="block text-xs font-bold text-gray-600">از تاریخ</span>
                    <input
                      type="date"
                      value={filters.date_from}
                      onChange={(event) => setFilters((current) => ({ ...current, date_from: event.target.value }))}
                      className="w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
                    />
                  </label>
                  <label className="space-y-1">
                    <span className="block text-xs font-bold text-gray-600">تا تاریخ</span>
                    <input
                      type="date"
                      value={filters.date_to}
                      onChange={(event) => setFilters((current) => ({ ...current, date_to: event.target.value }))}
                      className="w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
                    />
                  </label>
                  <label className="space-y-1">
                    <span className="block text-xs font-bold text-gray-600">وضعیت سفارش</span>
                    <select
                      value={filters.status}
                      onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
                      className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
                    >
                      <option value="">همه وضعیت‌ها</option>
                      {Object.entries(statusLabel).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="overflow-auto">
  <table className="w-full text-center text-sm">
    <thead className="bg-gray-50">
      <tr className="text-xs text-gray-600">
        <th className="px-4 py-3 text-center">ردیف</th>
        <th className="px-4 py-3 text-center">شماره</th>
        <th className="px-4 py-3 text-center">وضعیت</th>
        <th className="px-4 py-3 text-center">تاریخ</th>
        <th className="px-4 py-3 text-center">مبلغ</th>
        <th className="px-4 py-3 text-center">عملیات</th>
      </tr>
    </thead>

    <tbody>
      {loading && (
        <tr>
          <td colSpan={6} className="px-4 py-8 text-center text-xs text-gray-500">
            در حال دریافت...
          </td>
        </tr>
      )}

      {!loading && list.map((it, index) => (
        <tr
          key={it.id}
          className="border-t border-gray-100 hover:bg-blue-50/40 transition"
        >
          <td className="px-4 py-3 text-gray-500">
            {(index + 1).toLocaleString('fa-IR')}
          </td>
          <td className="px-4 py-3 font-bold text-blue-600">
            <Link
              to={`/preinvoice/${it.id}`}
              className="hover:underline"
            >
              #{it.id}
            </Link>
          </td>

          <td className="px-4 py-3">
            <span className="inline-flex px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-700">
              {statusLabel[it.status] || it.status}
            </span>
          </td>

          <td className="px-4 py-3 text-gray-700">
            {formatDate(it.created_at || it.updated_at)}
          </td>

          <td className="px-4 py-3 font-semibold text-emerald-700">
            {Number(it.total_amount || 0).toLocaleString('fa-IR')} ریال
          </td>

          <td className="px-4 py-3">
            <Link
              to={`/preinvoice/${it.id}`}
              className="px-3 py-1 rounded-lg bg-blue-50 text-[#005bbb] text-xs font-bold hover:bg-blue-100"
            >
              مشاهده جزئیات
            </Link>
          </td>
        </tr>
      ))}

      {!loading && list.length === 0 && (
        <tr>
          <td colSpan={6} className="px-4 py-8 text-center text-xs text-gray-500">
            هنوز پیش‌فاکتوری ثبت نکرده‌اید.
          </td>
        </tr>
      )}
    </tbody>
  </table>
</div>

              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-6">
                <div className="font-extrabold text-gray-900 mb-4">ویرایش پروفایل</div>
                {!user?.profile_completed_at && (
                  <div className="mb-5 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-xs leading-6 text-blue-800">
                    برای تکمیل پروفایل، اطلاعات هویتی و نشانی محل خود را ثبت کنید.
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-600">نام</label>
                    <input
                      value={profile.first_name}
                      onChange={(e) => setProfile((p) => ({ ...p, first_name: e.target.value }))}
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005bbb]/30"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">نام خانوادگی</label>
                    <input
                      value={profile.last_name}
                      onChange={(e) => setProfile((p) => ({ ...p, last_name: e.target.value }))}
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005bbb]/30"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs text-gray-600">ایمیل</label>
                    <input
                      value={profile.email}
                      onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005bbb]/30"
                      placeholder="اختیاری"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs text-gray-600">استان و شهرستان</label>
                    <button
                      type="button"
                      onClick={() => setAddressPickerOpen(true)}
                      className="mt-1 flex w-full items-center justify-between rounded-2xl border border-gray-200 px-4 py-3 text-right text-sm transition hover:border-blue-300 hover:bg-blue-50/40"
                    >
                      <span className={profile.county_name ? 'font-bold text-gray-900' : 'text-gray-400'}>
                        {profile.county_name ? `${profile.province_name}، ${profile.county_name}` : 'انتخاب از روی نقشه ایران'}
                      </span>
                      <span className="text-xs font-bold text-[#005bbb]">انتخاب</span>
                    </button>
                    {profile.address_line ? (
                      <div className="mt-2 rounded-2xl bg-gray-50 px-4 py-3 text-xs leading-6 text-gray-600">
                        {profile.address_line}
                      </div>
                    ) : null}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs text-gray-600">کد پستی</label>
                    <input
                      inputMode="numeric"
                      maxLength={10}
                      value={profile.postal_code}
                      onChange={(e) => setProfile((p) => ({ ...p, postal_code: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005bbb]/30"
                      placeholder="۱۰ رقم بدون خط تیره"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <div className="text-xs text-gray-500 mb-2">شماره همراه: <span className="font-bold">{user?.phone}</span></div>
                  </div>
                </div>
                <button
                  onClick={saveProfile}
                  disabled={loading}
                  className={`mt-5 w-full rounded-2xl py-3 text-sm font-extrabold transition ${
                    loading ? 'bg-gray-200 text-gray-500' : 'bg-emerald-600 text-white hover:bg-emerald-700'
                  }`}
                >
                  {loading ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}
